import { io } from 'socket.io-client';

/**
 * Get the server URL based on environment
 */
const getServerUrl = () => {
  // In production, use the same origin
  if (process.env.NODE_ENV === 'production') {
    return window.location.origin;
  }
  
  // For Docker development, check if we're running in a container
  // The hostname will be 'app' if we're inside the container
  if (process.env.REACT_APP_DOCKER === 'true') {
    return 'http://localhost:5000';
  }
  
  // Default for local development
  return 'http://localhost:5000';
};

/**
 * Test utility to verify server connectivity
 * Run this from the browser console with:
 * import('./utils/testConnection.js').then(module => module.testServerConnection())
 */
export const testServerConnection = async () => {
  console.log('Starting server connection test...');
  
  // Get server URL
  const serverUrl = getServerUrl();
  console.log(`Using server URL: ${serverUrl}`);
  
  // Test 1: Check health endpoint with fetch
  try {
    console.log('Test 1: Checking health endpoint...');
    
    // Use fetch instead of axios
    const response = await fetch(`${serverUrl}/health`, {
      method: 'GET',
      headers: { 
        'Cache-Control': 'no-cache',
        'Accept': 'application/json'
      },
      mode: 'cors' // Explicitly set CORS mode
    });
    
    if (!response.ok) {
      throw new Error(`Server responded with status ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ Health endpoint response:', data);
    
    // Skip socket test if health check passes
    console.log('Health check passed, skipping socket test for simplicity');
    return true;
  } catch (error) {
    console.error('❌ Health endpoint error:', error.message);
    console.error('Trying alternative approach...');
    
    // Try without CORS mode as a fallback
    try {
      const response = await fetch(`${serverUrl}/health`, {
        method: 'GET',
        headers: { 
          'Cache-Control': 'no-cache',
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log('✅ Alternative health check succeeded');
        return true;
      }
      
      console.error('❌ Alternative health check failed');
    } catch (altError) {
      console.error('❌ Alternative approach failed:', altError.message);
    }
    
    // If all HTTP checks fail, try socket connection
    return testSocketConnection(serverUrl);
  }
};

// Separate function for socket testing
const testSocketConnection = async (serverUrl) => {
  try {
    console.log('Test 2: Testing socket.io connection...');
    
    // Clean up any existing socket connections first
    const existingSockets = window._debugSockets || [];
    existingSockets.forEach(s => {
      try {
        if (s && !s.disconnected) {
          console.log(`Cleaning up existing socket: ${s.id}`);
          s.disconnect();
        }
      } catch (e) {
        console.error('Error cleaning up socket:', e);
      }
    });
    
    // Create a fresh socket connection
    const socket = io(serverUrl, {
      transports: ['websocket', 'polling'],
      timeout: 5000,
      forceNew: true,
      reconnection: false // Don't auto-reconnect for this test
    });
    
    // Store for debugging
    window._debugSockets = window._debugSockets || [];
    window._debugSockets.push(socket);
    
    return new Promise((resolve) => {
      // Set timeout first to ensure it's registered
      const timeoutId = setTimeout(() => {
        console.error('❌ Socket connection timeout after 5 seconds');
        try {
          socket.disconnect();
        } catch (e) {
          console.error('Error disconnecting socket:', e);
        }
        resolve(false);
      }, 5000);
      
      socket.on('connect', () => {
        console.log('✅ Socket connected successfully:', socket.id);
        console.log('Socket transport:', socket.io.engine.transport.name);
        clearTimeout(timeoutId);
        socket.disconnect();
        resolve(true);
      });
      
      socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
        clearTimeout(timeoutId);
        socket.disconnect();
        resolve(false);
      });
    });
  } catch (error) {
    console.error('❌ Socket creation error:', error.message);
    return false;
  }
};

// Helper function to check if server is running
export const isServerRunning = async () => {
  try {
    const serverUrl = getServerUrl();
    
    const response = await fetch(`${serverUrl}/health`, { 
      method: 'GET',
      headers: { 
        'Cache-Control': 'no-cache',
        'Accept': 'application/json'
      },
      mode: 'cors'
    });
    
    return response.ok;
  } catch (error) {
    return false;
  }
};

export default testServerConnection; 